import { CREDIT_CARD_KEY } from '@pano-project/utils/getCcMethodDisplayTitle';
import { EWalletConfigMode } from './wallet-types';

/**
 * PRX-23109: shared, single-source-of-truth boolean derivations for
 * "is a digital wallet method currently showable / active", used by
 * credit-card-page.js, SubmitButton.js, SubmitButtonWithTotal.js and
 * DigitalWalletButton.tsx.
 *
 * Before this fix, each of those four files independently re-derived an
 * equivalent expression inline, and every one of them also required
 * `gPay.isReadyToPay` / `applePay.isReadyToPay` to be true. That flag is
 * only ever set by the PG HPF SDK's async 'onTokenizationAvailability'
 * postMessage event (see useDigitalWallets.onTokenizationAvailabilityEvent),
 * which may never fire for the wallet the customer actually selected --
 * silently keeping the card form visible, the billing panel visible, the
 * regular Submit button in place and the wallet button unreachable even
 * after the customer picked google_pay_cc/apple_pay_cc from the
 * payment-method list.
 *
 * Device compatibility is already confirmed before a wallet method can even
 * appear as a selectable payment-method-list tile (task 2.4.3,
 * checkDigitalWalletDeviceCompatibility / filterUnsupportedDigitalWalletGateways),
 * so re-gating basic visibility on that separate, older, event-driven
 * mechanism a second time was both redundant and the root cause of the
 * problem described above. These
 * helpers intentionally take no `isReadyToPay` input at all -- centralizing
 * the formula here (instead of four separately-maintained inline copies)
 * makes that kind of drift much harder to reintroduce.
 */

/**
 * Is digital-wallet functionality showable at all right now? `precondition`
 * is the caller-specific gate (e.g. "this cc gateway supports wallets and
 * we're not in the amount-first layout", or "a wallet instrument is
 * currently selected") -- this only adds "...and at least one wallet
 * provider is enabled on the gateway" on top of it.
 */
export const isDigitalWalletShowable = (
  precondition: boolean,
  gPayEnabled: boolean,
  applePayEnabled: boolean,
): boolean => precondition && (gPayEnabled || applePayEnabled);

/**
 * Is this specific wallet provider (Google Pay / Apple Pay) the one the
 * customer actually has selected right now?
 */
export const isWalletProviderActive = (
  digitalWalletSelected: boolean,
  providerSelected: boolean,
): boolean => digitalWalletSelected && providerSelected;

/**
 * PRX-23109: is this specific wallet provider (Google Pay / Apple Pay)
 * the one selected on the payment-method list *right now*, read directly off
 * `currentGateway.payment_method_key` -- NOT off the Redux-synced
 * `paymentInstrument` that gPay.isSelected/applePay.isSelected (useGPay /
 * useApplePay) are built from.
 *
 * Switching between Google Pay and Apple Pay used to leave the newly
 * selected wallet's button never rendering (whichever was picked first kept
 * working; the other stayed permanently blank). `paymentInstrument` is kept
 * in sync with `payment_method_key` by a `useEffect` in useDigitalWallets
 * that dispatches `updateDigitalWallet` -- so it only updates a render cycle
 * *after* `payment_method_key` changes. credit-card-form.js's own PG HPF SDK
 * reinit effect reads `payment_method_key` directly and reconstructs the
 * SDK's container set (`{google_pay: '#google-pay'}` /
 * `{apple_pay: '#apple-pay'}`) in the *same* render's passive-effect phase --
 * before ExternalSlot's `useLayoutEffect`, gated on the lagged isSelected,
 * has mounted the matching container into the DOM. The SDK looks up its
 * container once, synchronously, in its constructor and never retries, so it
 * silently fails whenever the two effects land on different commits, which
 * they always do on a wallet <-> wallet switch (nothing else happens to
 * delay the SDK reinit far enough to let `paymentInstrument` catch up
 * first).
 *
 * Only container-visibility gating (DigitalWalletButton's `show` prop) may
 * use this. Everything else that answers "which wallet is selected" for the
 * *separate-payment-method path specifically* (gPay.isSelected/
 * applePay.isSelected themselves, the SubmitButton/SubmitButtonWithTotal
 * active-CTA checks via isWalletProviderActive above) must keep depending
 * on `paymentInstrument` -- that's what actually gets tokenized/submitted
 * for that path, and it's fine (expected, even) for it to lag the list
 * selection by one render until the tokenization flow catches up.
 *
 * `digitalW.isSelected`, `onDigitalWalletTokenized`, `sendTransactionResults`
 * and `getProviderName` (in useDigitalWallets.ts) are different: they must
 * answer "which wallet is active right now" for *either* configuration, so
 * they resolve through `effectiveWalletInstrument` (embeddedToggleInstrument
 * when the embedded configuration is active, `paymentInstrument`
 * otherwise) -- see that file's own doc comment.
 */
export const isWalletProviderSelectedNow = (
  selectedPaymentMethodKey: string | null | undefined,
  providerKey: string,
  providerEnabled: boolean,
): boolean => providerEnabled && selectedPaymentMethodKey === providerKey;

/**
 * PRX-23109 (Revision 2, N2): is the old, toggle-inside-Credit-Card
 * configuration actually in play right now? True when the currently
 * selected gateway is Credit Card, its own configuration lists a supported
 * wallet provider (EWalletConfigMode.Embedded -- see
 * digital-wallets/helpers.ts's getWalletConfigMode), and this is a deposit.
 *
 * A withdrawal is deliberately excluded, mirroring how the newer,
 * separate-payment-method path already restricts its own live wallet
 * button/container to deposits (a withdrawal to a saved card has no live
 * wallet payment to tokenize) -- see N4 for the withdrawal-specific,
 * saved-card-list behavior this configuration gets instead.
 */
export const isEmbeddedWalletConfigActive = (
  walletConfigMode: EWalletConfigMode,
  selectedPaymentMethodKey: string | null | undefined,
  isPayIn: boolean,
): boolean =>
  isPayIn &&
  walletConfigMode === EWalletConfigMode.Embedded &&
  selectedPaymentMethodKey === CREDIT_CARD_KEY;

/**
 * PRX-23109 (user-reported, master-parity fix): is at least one wallet
 * provider actually confirmed ready to pay on this device? Master's own
 * Credit-Card-only toggle always required this (`gPay.isReadyToPay &&
 * gPay.isEnabled`, same for Apple Pay) before showing its in-page toggle at
 * all -- `isEmbeddedWalletConfigActive` above deliberately dropped this
 * requirement (see its own file-level doc comment), because for the newer,
 * separate-payment-method path device compatibility is already confirmed
 * before that method's own tile can even appear in the list. Credit Card's
 * embedded toggle has no such pre-filtering -- Credit Card stays listed
 * regardless of wallet support -- so the toggle itself still needs this
 * check, exactly as master had it, or it shows a "choose Card or a wallet"
 * screen when neither wallet is actually usable on this device.
 *
 * Deliberately excluded from `isEmbeddedWalletConfigActive` itself: two of
 * its callers (credit-card-form.js's PCI container-mode/reinit logic) must
 * keep constructing both wallets' containers regardless of readiness --
 * readiness itself is only ever discovered *after* that container exists.
 * Only the toggle's own visibility/active-state callers need this on top.
 */
export const isEmbeddedWalletReady = (
  gPayEnabled: boolean,
  gPayReady: boolean,
  applePayEnabled: boolean,
  applePayReady: boolean,
): boolean => (gPayEnabled && gPayReady) || (applePayEnabled && applePayReady);

/**
 * PRX-23109 (T4.33, user-reported): is a saved-card template the customer's
 * actual, already-made selection right now, in the specific sense that
 * matters for the embedded (toggle-inside-Credit-Card) configuration --
 * mirrors useAmountFirstSteps.ts's own `isSavedCardWithTemplate` formula
 * exactly (`method === 'card-method'` and a non-empty
 * `currentGateway.recently_used_template`).
 *
 * Every embedded-configuration writer of `embeddedToggleInstrument`
 * (useDigitalWallets.ts's own declarative default-selection effect and its
 * `onTokenizationAvailabilityEvent` handler) must treat this as true as a
 * hard override: once the customer has picked an existing saved template,
 * that already commits them to paying with that known card -- the embedded
 * toggle's own "pick a ready wallet by default" behavior must never run
 * for that selection, and any value it already wrote earlier for the SAME
 * underlying Credit Card gateway (see the reset effect built on this
 * function, in useDigitalWallets.ts, for why a genuine gateway switch alone
 * cannot be relied on to have cleared it) must be corrected back to Card.
 * Every consumer that reads `embeddedToggleInstrument` -- directly
 * (SubmitButton.js, SubmitButtonWithTotal.js, DigitalWalletButton.tsx,
 * getActiveWalletInstrument) or through useDigitalWallets.ts's own derived
 * `isEmbeddedWalletToggleSelected`/`isEmbeddedWalletActive` -- is fixed by
 * keeping this single, shared field correct at its source, instead of
 * separately re-deriving this same exclusion in each of those independent
 * call sites.
 *
 * Deliberately scoped to `layout === 'amount-first'` only: `layout ===
 * 'panoramic'` shows the embedded toggle and the saved-card template picker
 * on the same page rather than as separate steps, where the toggle
 * defaulting to a ready wallet alongside an already-selected template is
 * original, intentional behavior carried over from the pre-project
 * Credit-Card-only toggle (see T4.32's own doc comment) -- narrowing this
 * check to amount-first keeps panoramic completely unaffected.
 *
 * A plain function over primitive Redux values, not a hook: useDigitalWallets.ts
 * needs this from inside its own body, and useAmountFirstSteps.ts (which
 * already computes the equivalent `isSavedCardWithTemplate`) itself calls
 * useDigitalWallets() internally -- importing that hook back into
 * useDigitalWallets.ts to reuse its own flag would be a circular hook
 * dependency.
 *
 * PRX-23109 (T4.35, user-reported, regression from T4.33): `recentlyUsedTemplate`
 * alone only answers "does this gateway *have* a recently-used template
 * available at all" -- not "is the customer *currently choosing* to use it
 * right now". Credit Card selected, a saved template genuinely picked once,
 * then the customer explicitly switches to "add a new card" for the SAME
 * underlying gateway (credit-card-form.js's own local `isTemplate`/
 * `isAddCardMode` state, mirrored into Redux as `isAddCardModeSelected` --
 * see that component's own consumption of `onAddCardModeChange`):
 * `currentGateway.recently_used_template` does not necessarily change for
 * that switch, so without this extra input this function stayed `true`
 * (wrongly still treating the template as the active selection) even once
 * the customer moved on from it, permanently suppressing the embedded
 * toggle's own ready-wallet default for that gateway. `isAddCardModeSelected`
 * is a hard override in the "add a new card" direction only -- it never
 * forces `true` on its own; a genuinely selected template still requires a
 * non-empty `recentlyUsedTemplate` exactly as before. Defaults to `false`
 * (matching credit-card-form.js's own initial `isTemplate = true` default
 * and this field's own Redux default) so every existing caller that does
 * not pass it keeps its exact prior behavior.
 */
export const isSavedCardTemplateActiveForEmbeddedWallet = (
  layout: string | null | undefined,
  method: string | null | undefined,
  recentlyUsedTemplate: unknown,
  isAddCardModeSelected: boolean = false,
): boolean =>
  layout === 'amount-first' &&
  method === 'card-method' &&
  !isAddCardModeSelected &&
  Array.isArray(recentlyUsedTemplate) &&
  recentlyUsedTemplate.length > 0;
